import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { StatutExploitation } from '@genviz/shared/enums';

import { useExploitations } from '../api/queries';
import type { LigneExploitation } from '../api/types';
import { BadgeStatutExploitation, PastilleNombre } from '../components/Badges';
import { Carte } from '../components/Carte';
import { Saisie, Selecteur } from '../components/Champs';
import { MessageErreur } from '../components/MessageErreur';
import { Tableau, type Colonne } from '../components/Tableau';
import { LIBELLE_STATUT_EXPLOITATION, VALEUR_ABSENTE } from '../i18n/fr';
import { formaterDate, formaterNombre, formaterRelatif } from '../lib/format';
import { useDifferer } from '../lib/useDifferer';

const OPTIONS_STATUT = Object.values(StatutExploitation).map((s) => ({
  valeur: s,
  libelle: LIBELLE_STATUT_EXPLOITATION[s],
}));

/**
 * Liste des exploitations clientes.
 *
 * La colonne « appareils critiques » est volontairement placée avant l'ancienneté du compte :
 * ce qui intéresse le support, ce n'est pas qui est client depuis longtemps, c'est chez qui ça
 * va mal en ce moment.
 */
export function ExploitationsPage(): JSX.Element {
  const naviguer = useNavigate();
  const [parametres, setParametres] = useSearchParams();

  const q = parametres.get('q') ?? '';
  const statut = parametres.get('statut') ?? '';

  const filtres = useMemo(
    () => ({
      ...(q ? { q } : {}),
      ...(statut ? { statut: statut as StatutExploitation } : {}),
    }),
    [q, statut],
  );

  const { data, error, isPending } = useExploitations(filtres);

  const majFiltre = (cle: string, valeur: string): void => {
    const suivants = new URLSearchParams(parametres);
    if (valeur === '') suivants.delete(cle);
    else suivants.set(cle, valeur);
    setParametres(suivants, { replace: true });
  };

  // Même amortissement que l'écran de stock : la frappe reste locale, l'URL et la requête ne
  // suivent qu'une fois la saisie stabilisée. `dernierPousse` arbitre les deux sens.
  const [recherche, setRecherche] = useState(q);
  const rechercheDifferee = useDifferer(recherche.trim());
  const dernierPousse = useRef(q);

  useEffect(() => {
    if (rechercheDifferee === dernierPousse.current) return;
    dernierPousse.current = rechercheDifferee;
    setParametres(
      (courants) => {
        const suivants = new URLSearchParams(courants);
        if (rechercheDifferee === '') suivants.delete('q');
        else suivants.set('q', rechercheDifferee);
        return suivants;
      },
      { replace: true },
    );
  }, [rechercheDifferee, setParametres]);

  useEffect(() => {
    if (q === dernierPousse.current) return;
    dernierPousse.current = q;
    setRecherche(q);
  }, [q]);

  const colonnes: Array<Colonne<LigneExploitation>> = [
    { cle: 'nom', entete: 'Exploitation', rendu: (e) => <strong>{e.nom}</strong> },
    { cle: 'statut', entete: 'Statut', rendu: (e) => <BadgeStatutExploitation statut={e.statut} /> },
    {
      cle: 'admin',
      entete: 'Administrateur',
      rendu: (e) => <span className="gv-mono gv-corps-s">{e.administrateur_email ?? VALEUR_ABSENTE}</span>,
    },
    {
      cle: 'utilisateurs',
      entete: 'Utilisateurs',
      rendu: (e) => formaterNombre(e.nb_utilisateurs),
      numerique: true,
    },
    {
      cle: 'appareils',
      entete: 'Appareils',
      rendu: (e) => formaterNombre(e.nb_electrificateurs),
      numerique: true,
    },
    {
      cle: 'critiques',
      entete: 'Critiques',
      rendu: (e) => <PastilleNombre valeur={e.nb_appareils_critiques} />,
      numerique: true,
    },
    {
      cle: 'activite',
      entete: 'Dernière activité',
      rendu: (e) =>
        e.derniere_activite_at ? formaterRelatif(e.derniere_activite_at) : 'aucune',
    },
    { cle: 'creation', entete: 'Créée le', rendu: (e) => formaterDate(e.created_at) },
  ];

  return (
    <>
      <header className="gv-entete">
        <div className="gv-pile gv-pile--serree">
          <h1 className="gv-h1">Exploitations</h1>
          <span className="gv-corps-s gv-secondaire">
            {formaterNombre(data?.total ?? 0)} exploitation(s)
          </span>
        </div>
      </header>

      <Carte>
        <div className="gv-filtres">
          <div className="gv-filtres__recherche">
            <Saisie
              label="Recherche"
              placeholder="Nom de l’exploitation ou e-mail de l’administrateur"
              value={recherche}
              onChange={(e) => {
                setRecherche(e.currentTarget.value);
              }}
            />
          </div>
          <Selecteur
            label="Statut"
            optionVide="Tous les statuts"
            options={OPTIONS_STATUT}
            value={statut}
            onChange={(e) => {
              majFiltre('statut', e.currentTarget.value);
            }}
          />
        </div>
      </Carte>

      {error ? <MessageErreur erreur={error} /> : null}

      <Carte sansPadding>
        <Tableau
          colonnes={colonnes}
          lignes={data?.items ?? []}
          cleDe={(e) => e.id}
          chargement={isPending}
          titreVide="Aucune exploitation"
          descriptionVide="Aucune exploitation ne correspond à ce filtre."
          surClic={(e) => {
            naviguer(`/exploitations/${e.id}`);
          }}
        />
      </Carte>
    </>
  );
}
